import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMember } from '../contexts/MemberContext';
import { supabase } from '../utils/supabaseClient';

const MemberProfile = () => {
  const navigate = useNavigate();
  const { logout } = useMember();
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    joinDate: '',
    avatar: '👩‍🦰',
    allergies: [],
    dietaryPreferences: [],
    fitnessGoals: [],
    healthConditions: [],
    fitnessLevel: '',
    preferredWorkoutTime: '',
    workoutDuration: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(profileData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customAllergies, setCustomAllergies] = useState('');

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [draftAvatarUrl, setDraftAvatarUrl] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProfileFromSupabase();
  }, []);

  // Load profile data from Supabase
  const loadProfileFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('🔍 Loading profile for user:', user?.id);
      
      if (!user) {
        console.error('❌ No authenticated user');
        setLoading(false);
        return;
      }

      const { data: member, error } = await supabase
        .from('members')
        .select('*')
        .eq('auth_id', user.id)
        .single();

      if (error) {
        console.error('❌ Error loading profile:', error);
        console.error('Query was: members table, auth_id =', user.id);
        setLoading(false);
        return;
      }

      console.log('✅ Profile loaded successfully:', member);

      if (member) {
        // Separate standard allergies from custom ones
        const allAllergies = member.allergies || [];
        const standardAllergies = allAllergies.filter(a => allergyOptions.includes(a));
        const customAllergyList = allAllergies.filter(a => !allergyOptions.includes(a));
        
        const profile = {
          name: `${member.first_name} ${member.last_name || ''}`.trim(),
          email: member.email,
          joinDate: new Date(member.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          avatar: '👩‍🦰',
          allergies: standardAllergies,
          dietaryPreferences: member.dietary_preferences || [],
          fitnessGoals: member.fitness_goals || [],
          healthConditions: member.health_conditions || [],
          fitnessLevel: member.fitness_level || '',
          preferredWorkoutTime: member.preferred_workout_time || '',
          workoutDuration: member.workout_duration || ''
        };
        
        // Set custom allergies if any exist
        if (customAllergyList.length > 0) {
          setCustomAllergies(customAllergyList.join(', '));
        }
        
        setProfileData(profile);
        setEditData(profile);
        
        // Load avatar
        if (member.avatar_url) {
          setAvatarUrl(member.avatar_url);
          setDraftAvatarUrl(member.avatar_url);
          // Also update localStorage for dashboard
          localStorage.setItem('memberAvatarUrl', member.avatar_url);
        }
        
        // Update localStorage name for dashboard
        localStorage.setItem('memberProfileName', profile.name);
      }
    } catch (error) {
      console.error('Error in loadProfileFromSupabase:', error);
    } finally {
      setLoading(false);
    }
  };

  const allergyOptions = [
    'Dairy', 'Gluten', 'Nuts', 'Eggs', 'Soy', 'Shellfish', 'Fish', 'Sesame', 'Sulfites', 'Nightshades', 'Other'
  ];

  const dietaryOptions = [
    'Anti-inflammatory', 'Low-histamine', 'Paleo', 'Keto', 'Vegan', 'Vegetarian', 'Mediterranean', 'Organic', 'Low-FODMAP', 'AIP'
  ];

  const fitnessGoalOptions = [
    'Weight Loss', 'Muscle Building', 'Strength', 'Flexibility', 'Stress Relief', 'Gentle Movement', 'Endurance', 'Balance'
  ];

  const healthConditionOptions = [
    'Eczema', 'Psoriasis', 'Food Sensitivities', 'IBS', 'Autoimmune', 'Chronic Fatigue', 'Anxiety', 'Depression'
  ];

  const handleSave = async () => {
    setSaving(true);
    console.log('💾 Starting profile save...');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('🔍 User for save:', user?.id);
      
      if (!user) {
        console.error('❌ No authenticated user');
        alert('Not logged in. Please log in and try again.');
        setSaving(false);
        return;
      }

      // Parse name into first and last
      const nameParts = (editData.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Combine standard allergies with custom allergies
      let finalAllergies = [...(editData.allergies || [])];
      
      // If 'Other' is checked and there's custom text, add custom allergies
      if (finalAllergies.includes('Other') && customAllergies.trim()) {
        // Remove 'Other' from the list
        finalAllergies = finalAllergies.filter(a => a !== 'Other');
        // Parse and add custom allergies (split by comma)
        const customAllergyList = customAllergies
          .split(',')
          .map(a => a.trim())
          .filter(a => a.length > 0);
        finalAllergies = [...finalAllergies, ...customAllergyList];
      } else if (finalAllergies.includes('Other')) {
        // If 'Other' is checked but no custom text, just remove 'Other'
        finalAllergies = finalAllergies.filter(a => a !== 'Other');
      }

      const updateData = {
        first_name: firstName,
        last_name: lastName,
        allergies: finalAllergies,
        dietary_preferences: editData.dietaryPreferences || [],
        fitness_goals: editData.fitnessGoals || [],
        health_conditions: editData.healthConditions || [],
        fitness_level: editData.fitnessLevel || '',
        preferred_workout_time: editData.preferredWorkoutTime || '',
        workout_duration: editData.workoutDuration || '',
        avatar_url: draftAvatarUrl || null,
        updated_at: new Date().toISOString()
      };

      console.log('📝 Updating with data:', updateData);
      console.log('🎯 Query: UPDATE members WHERE auth_id =', user.id);

      // Update member profile in Supabase
      const { data: updatedData, error } = await supabase
        .from('members')
        .update(updateData)
        .eq('auth_id', user.id)
        .select();

      if (error) {
        console.error('❌ Error saving profile:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        alert(`Failed to save profile: ${error.message}`);
        setSaving(false);
        return;
      }

      console.log('✅ Profile saved successfully!', updatedData);
      console.log('📊 Rows affected:', updatedData?.length || 0);

      // Update local state
      setProfileData(editData);
      setAvatarUrl(draftAvatarUrl || null);
      
      // Update localStorage for dashboard
      if (editData?.name) {
        localStorage.setItem('memberProfileName', editData.name);
        window.dispatchEvent(new CustomEvent('memberProfileNameUpdated', { detail: { name: editData.name } }));
      }
      
      if (draftAvatarUrl) {
        localStorage.setItem('memberAvatarUrl', draftAvatarUrl);
      } else {
        localStorage.removeItem('memberAvatarUrl');
      }
      
      // Notify other components
      window.dispatchEvent(new CustomEvent('memberAvatarUpdated', { detail: { url: draftAvatarUrl || null } }));
      
      setIsEditing(false);
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Error in handleSave:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData(profileData);
    setDraftAvatarUrl(avatarUrl || null);
    // Reset custom allergies to what was loaded
    const allAllergies = profileData.allergies || [];
    const customAllergyList = allAllergies.filter(a => !allergyOptions.slice(0, -1).includes(a));
    setCustomAllergies(customAllergyList.join(', '));
    setIsEditing(false);
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    try {
      const user = supabase.auth.getUser ? (await supabase.auth.getUser()).data.user : null;
      if (!user) {
        alert('Please log in to upload a profile picture');
        return;
      }

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload image. Please try again.');
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setDraftAvatarUrl(urlData.publicUrl);
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleRemoveAvatar = () => {
    setDraftAvatarUrl(null);
  };

  const toggleArrayItem = (array, item, setFunction) => {
    const newArray = array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
    setFunction(newArray);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-sage-100 pt-20 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
          <p className="text-charcoal-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-sage-100 pt-20 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <RouterLink
              to="/dashboard"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </RouterLink>
            <h1 className="text-3xl font-playfair font-semibold text-charcoal-900">My Profile</h1>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-sage-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-sage-700 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <div className="text-center">
                { (draftAvatarUrl || avatarUrl) ? (
                  <img
                    src={draftAvatarUrl || avatarUrl}
                    alt="Profile"
                    className="w-28 h-28 rounded-full object-cover mx-auto mb-4 ring-2 ring-sage-300"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-r from-sage-400 to-beige-400 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">
                    {profileData.avatar}
                  </div>
                )}

                {!isEditing ? (
                  <>
                    <h2 className="text-xl font-semibold text-charcoal-900 mb-1">{profileData.name}</h2>
                    <p className="text-charcoal-600 text-sm mb-2">{profileData.email}</p>
                  </>
                ) : (
                  <div className="space-y-3 mb-2">
                    <div>
                      <label className="block text-xs text-charcoal-600 text-left mb-1">Name</label>
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-charcoal-600 text-left mb-1">Email</label>
                      <input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
                      />
                    </div>
                  </div>
                )}
                <p className="text-charcoal-500 text-xs">Member since {profileData.joinDate}</p>

                {isEditing && (
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <label className="inline-flex items-center px-3 py-2 rounded-xl bg-sage-600 text-white text-sm font-medium cursor-pointer hover:bg-sage-700">
                      Change Photo
                      <input type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} />
                    </label>
                    { (draftAvatarUrl || avatarUrl) && (
                      <button type="button" onClick={handleRemoveAvatar} className="text-sm text-red-600 hover:text-red-700">
                        Remove Photo
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3">
                <div className="bg-sage-50 rounded-xl p-3">
                  <div className="text-sm font-medium text-sage-800 mb-1">Fitness Level</div>
                  <div className="text-sm text-sage-600">{profileData.fitnessLevel}</div>
                </div>
                <div className="bg-beige-50 rounded-xl p-3">
                  <div className="text-sm font-medium text-beige-800 mb-1">Preferred Workout Time</div>
                  <div className="text-sm text-beige-600">{profileData.preferredWorkoutTime}</div>
                </div>
                <div className="bg-sage-50 rounded-xl p-3">
                  <div className="text-sm font-medium text-sage-800 mb-1">Workout Duration</div>
                  <div className="text-sm text-sage-600">{profileData.workoutDuration}</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Allergies & Dietary Preferences */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Allergies & Dietary Preferences</h3>
              
              {!isEditing ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-charcoal-700 mb-2">Allergies</h4>
                    <div className="flex flex-wrap gap-2">
                      {profileData.allergies.map((allergy) => (
                        <span key={allergy} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                          {allergy}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-charcoal-700 mb-2">Dietary Preferences</h4>
                    <div className="flex flex-wrap gap-2">
                      {profileData.dietaryPreferences.map((pref) => (
                        <span key={pref} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          {pref}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-charcoal-700 mb-2">Allergies</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {allergyOptions.map((allergy) => (
                        <label key={allergy} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editData.allergies.includes(allergy)}
                            onChange={() => toggleArrayItem(editData.allergies, allergy, (newAllergies) => 
                              setEditData({...editData, allergies: newAllergies})
                            )}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-charcoal-700">{allergy}</span>
                        </label>
                      ))}
                    </div>
                    {/* Custom Allergies Input */}
                    {editData.allergies.includes('Other') && (
                      <div className="mt-3">
                        <label className="block text-xs text-charcoal-600 mb-1">Specify other allergies (separate with commas)</label>
                        <input
                          type="text"
                          value={customAllergies}
                          onChange={(e) => setCustomAllergies(e.target.value)}
                          placeholder="e.g., Avocado, Kiwi, Latex"
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-charcoal-700 mb-2">Dietary Preferences</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {dietaryOptions.map((pref) => (
                        <label key={pref} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editData.dietaryPreferences.includes(pref)}
                            onChange={() => toggleArrayItem(editData.dietaryPreferences, pref, (newPrefs) => 
                              setEditData({...editData, dietaryPreferences: newPrefs})
                            )}
                            style={{ accentColor: '#a8b5a0' }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-charcoal-700">{pref}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Fitness Goals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Fitness Goals</h3>
              
              {!isEditing ? (
                <div className="flex flex-wrap gap-2">
                  {profileData.fitnessGoals.map((goal) => (
                    <span key={goal} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {goal}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {fitnessGoalOptions.map((goal) => (
                    <label key={goal} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.fitnessGoals.includes(goal)}
                        onChange={() => toggleArrayItem(editData.fitnessGoals, goal, (newGoals) => 
                          setEditData({...editData, fitnessGoals: newGoals})
                        )}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-charcoal-700">{goal}</span>
                    </label>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Health Conditions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Health Conditions</h3>
              
              {!isEditing ? (
                <div className="flex flex-wrap gap-2">
                  {profileData.healthConditions.map((condition) => (
                    <span key={condition} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                      {condition}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {healthConditionOptions.map((condition) => (
                    <label key={condition} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.healthConditions.includes(condition)}
                        onChange={() => toggleArrayItem(editData.healthConditions, condition, (newConditions) => 
                          setEditData({...editData, healthConditions: newConditions})
                        )}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-charcoal-700">{condition}</span>
                    </label>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Workout Preferences */}
            {isEditing && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
              >
                <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Workout Preferences</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Fitness Level</label>
                    <select
                      value={editData.fitnessLevel}
                      onChange={(e) => setEditData({...editData, fitnessLevel: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Preferred Time</label>
                    <select
                      value={editData.preferredWorkoutTime}
                      onChange={(e) => setEditData({...editData, preferredWorkoutTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
                    >
                      <option value="Morning">Morning</option>
                      <option value="Afternoon">Afternoon</option>
                      <option value="Evening">Evening</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Duration</label>
                    <select
                      value={editData.workoutDuration}
                      onChange={(e) => setEditData({...editData, workoutDuration: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
                    >
                      <option value="10-15 minutes">10-15 minutes</option>
                      <option value="20-30 minutes">20-30 minutes</option>
                      <option value="30-45 minutes">30-45 minutes</option>
                      <option value="45+ minutes">45+ minutes</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex space-x-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-sage-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-sage-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Logout Button - Always visible at bottom */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-8 pt-8 border-t border-gray-200"
            >
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="w-full flex items-center justify-center gap-2 bg-white hover:bg-rose-50 text-charcoal-700 hover:text-rose-700 py-3 px-6 rounded-xl font-medium border-2 border-gray-200 hover:border-rose-200 transition-all shadow-sm hover:shadow-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberProfile;
